import React, { useCallback, useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VoucherDialog, PaymentReceiptDialog } from "../components/VoucherDialogs";
import { Wallet, Landmark, HandCoins, FileText, AlertTriangle, TrendingUp, TrendingDown, Search, Boxes } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { OverdueTransfers } from "../components/BranchTools";

function Stat({ icon: Icon, label, value, tone = "default", sub }) {
  const tones = {
    default: "text-primary bg-primary/5",
    accent: "text-accent bg-accent/10",
    danger: "text-destructive bg-destructive/10",
  };
  const bar = { default: "bg-primary", accent: "bg-accent", danger: "bg-destructive" };
  return (
    <div className="relative rounded-lg border border-border bg-card p-5 shadow-sm overflow-hidden animate-fade-up hover:shadow-md transition-shadow">
      <span className={`absolute left-0 top-0 h-full w-1 ${bar[tone]}`} />
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={`p-2 rounded-md ${tones[tone]}`}><Icon size={16} /></span>
      </div>
      <div className="num text-2xl font-bold mt-2 text-foreground">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1 pt-2 border-t border-dashed border-border">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = hasWrite(user);
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const iso = (d) => d.toISOString().slice(0, 10);
  const todayStr = iso(new Date());
  const presets = () => {
    const now = new Date();
    return {
      today: { start: todayStr, end: todayStr },
      week: { start: iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6)), end: todayStr },
      month: { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: todayStr },
    };
  };
  const [range, setRange] = useState(() => ({ ...presets().today, key: "today" }));

  const load = useCallback(async (start = range.start, end = range.end) => {
    const [d, r] = await Promise.all([
      api.get("/dashboard", { params: { start, end } }),
      api.get("/reminders"),
    ]);
    setData(d.data);
    setReminders(r.data);
  }, [range.start, range.end]);
  useEffect(() => {
    load();
  }, [load]);

  const setPreset = (key) => { const p = presets()[key]; setRange({ ...p, key }); };

  const b = data?.balances;
  const pd = data?.period || b || {};
  const rangeLabel = range.key === "today" ? "Today" : range.key === "week" ? "This Week" : range.key === "month" ? "This Month" : "Custom range";
  return (
    <Layout reminders={reminders.length}>
      <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
        <PageHeader title={`Namaste, ${user?.name?.split(" ")[0]}`} subtitle={user?.company_name} />
        <OverdueTransfers />

        <div className="flex flex-wrap items-end gap-2 mb-6 animate-fade-up">
          {[["today", "Today"], ["week", "This Week"], ["month", "This Month"]].map(([k, l]) => (
            <button key={k} data-testid={`dash-range-${k}`} onClick={() => setPreset(k)}
              className={`h-9 px-4 rounded-md text-sm font-medium border transition-colors ${range.key === k ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{l}</button>
          ))}
          <div className="ml-1"><div className="text-[11px] text-muted-foreground mb-0.5">From</div>
            <input data-testid="dash-start" type="date" value={range.start}
              onChange={(e) => setRange({ ...range, start: e.target.value, key: "custom" })}
              className="h-9 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40" /></div>
          <div><div className="text-[11px] text-muted-foreground mb-0.5">To</div>
            <input data-testid="dash-end" type="date" value={range.end}
              onChange={(e) => setRange({ ...range, end: e.target.value, key: "custom" })}
              className="h-9 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40" /></div>
          <button data-testid="dash-search" onClick={() => load(range.start, range.end)}
            className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90 transition-colors self-end">
            <Search size={15} /> Search
          </button>
        </div>

        {reminders.length > 0 && (
          <div
            data-testid="reminder-banner"
            className="mb-6 rounded-xl border border-destructive/20 bg-destructive/5 p-4 flex items-start gap-3 animate-fade-up"
          >
            <AlertTriangle className="text-destructive mt-0.5" size={20} />
            <div className="text-sm">
              <span className="font-semibold text-destructive">
                {reminders.length} party credit limit{reminders.length > 1 ? "s" : ""} exceeded.
              </span>{" "}
              <span className="text-muted-foreground">
                {reminders.slice(0, 3).map((r) => r.name).join(", ")}
                {reminders.length > 3 ? "…" : ""} — follow up for recovery.
              </span>
            </div>
          </div>
        )}

        {canWrite && (
          <div className="rounded-lg border border-border bg-card shadow-sm p-5 mb-6 animate-fade-up">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Quick actions</div>
            <div className="flex flex-wrap gap-3">
              <VoucherDialog onDone={load} />
              <PaymentReceiptDialog onDone={load} />
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Vouchers &amp; receipts generate a signed PDF and post to the cash book automatically.
            </p>
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <Stat icon={Wallet} label="Cash in Hand" value={inr(b.cash_balance)} tone="accent" sub={`Opening ${inr(b.opening_balance)}`} />
              <Stat icon={Landmark} label="Bank Balance" value={inr(b.bank_balance)} />
              <Stat icon={HandCoins} label="Credit Outstanding" value={inr(data.total_credit_outstanding)} tone={data.over_limit_count ? "danger" : "default"} sub={`${data.credit_party_count} parties on credit`} />
              <Stat icon={FileText} label="Invoices Raised" value={data.invoice_count} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2 text-[hsl(var(--positive))] font-semibold text-sm pb-3 border-b border-border">
                  <TrendingUp size={16} /> Money In <span className="text-xs text-muted-foreground font-normal">· {rangeLabel}</span>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Cash receipts</span>
                  <span className="num font-semibold">{inr(pd.cash_in)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Bank receipts</span>
                  <span className="num font-semibold">{inr(pd.bank_in)}</span>
                </div>
              </div>
              <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2 text-destructive font-semibold text-sm pb-3 border-b border-border">
                  <TrendingDown size={16} /> Money Out <span className="text-xs text-muted-foreground font-normal">· {rangeLabel}</span>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Cash expenses</span>
                  <span className="num font-semibold">{inr(pd.cash_out)}</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Bank payments</span>
                  <span className="num font-semibold">{inr(pd.bank_out)}</span>
                </div>
              </div>
            </div>
            {data.assets_total > 0 && (
              <button data-testid="dash-assets-widget" onClick={() => navigate("/assets")}
                className="w-full mt-4 rounded-xl border border-border bg-card p-5 flex items-center justify-between text-left hover:border-accent/40 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="h-10 w-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center"><Boxes size={20} /></span>
                  <div>
                    <div className="font-medium text-sm">Assets out with employees</div>
                    <div className="text-xs text-muted-foreground">{data.asset_holders} employee{data.asset_holders === 1 ? "" : "s"} holding · {data.assets_total} assets total</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="num text-2xl font-bold text-accent" data-testid="dash-assets-out">{data.assets_out}</div>
                  <div className="text-xs text-muted-foreground">currently allotted</div>
                  {data.assets_by_category && Object.keys(data.assets_by_category).length > 0 && (
                    <div className="text-[11px] text-muted-foreground mt-1 flex gap-2 justify-end flex-wrap">
                      {Object.entries(data.assets_by_category).map(([c, n]) => <span key={c}>{c}: <b className="text-foreground num">{n}</b></span>)}
                    </div>
                  )}
                </div>
              </button>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
